import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { allArticles } from "@/mocks/articles";
import type { Article } from "@/hooks/useArticles";

export interface SearchSuggestion {
  id: string;
  title: string;
  entrepreneur: string;
  category: string;
  country: string;
  countryFlag: string;
  sector: string;
  image: string;
}

export type SearchResult = Article;

const isSupabaseConfigured = (): boolean => {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  return Boolean(url && key && url !== "" && key !== "");
};

function mapMockToSuggestion(m: (typeof allArticles)[0]): SearchSuggestion {
  return {
    id: m.id,
    title: m.title,
    entrepreneur: m.entrepreneur,
    category: m.category,
    country: m.country,
    countryFlag: m.countryFlag,
    sector: m.sector,
    image: m.image,
  };
}

// ── Autocomplete hook (debounced, max 6 suggestions) ──────────────────────────
export function useAutocomplete(query: string) {
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      const q = query.toLowerCase().trim();

      if (!isSupabaseConfigured()) {
        const results = allArticles
          .filter(
            (a) =>
              a.title.toLowerCase().includes(q) ||
              a.entrepreneur.toLowerCase().includes(q) ||
              a.sector.toLowerCase().includes(q) ||
              a.country.toLowerCase().includes(q)
          )
          .slice(0, 6)
          .map(mapMockToSuggestion);
        setSuggestions(results);
        setLoading(false);
        return;
      }

      try {
        const { data } = await supabase
          .from("articles")
          .select("id, title, entrepreneur, category, country, country_flag, sector, image")
          .eq("status", "published")
          .or(
            `title.ilike.%${q}%,entrepreneur.ilike.%${q}%,sector.ilike.%${q}%,country.ilike.%${q}%`
          )
          .order("views", { ascending: false })
          .limit(6);

        setSuggestions(
          (data ?? []).map((r) => ({
            id: String(r.id),
            title: String(r.title),
            entrepreneur: String(r.entrepreneur),
            category: String(r.category),
            country: String(r.country),
            countryFlag: String(r.country_flag ?? "🌍"),
            sector: String(r.sector),
            image: String(r.image),
          }))
        );
      } catch {
        const results = allArticles
          .filter((a) => a.title.toLowerCase().includes(q) || a.entrepreneur.toLowerCase().includes(q))
          .slice(0, 6)
          .map(mapMockToSuggestion);
        setSuggestions(results);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  return { suggestions, loading };
}

// ── Full search hook ──────────────────────────────────────────────────────────
export function useSearchResults(query: string, category: string, sector: string) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const search = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    const q = query.toLowerCase().trim();

    if (!isSupabaseConfigured()) {
      let data = allArticles.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.entrepreneur.toLowerCase().includes(q) ||
          a.country.toLowerCase().includes(q) ||
          a.sector.toLowerCase().includes(q)
      );
      if (category && category !== "Toutes catégories") data = data.filter((a) => a.category === category);
      if (sector && sector !== "Tous les secteurs") data = data.filter((a) => a.sector === sector);
      setTotal(data.length);
      setResults(
        data.map((a) => ({
          id: a.id,
          title: a.title,
          summary: a.summary,
          image: a.image,
          entrepreneur: a.entrepreneur,
          country: a.country,
          countryFlag: a.countryFlag,
          category: a.category,
          sector: a.sector,
          source: a.source,
          sourceUrl: a.sourceUrl,
          date: a.date,
          featured: a.featured,
          views: a.views,
          readTime: a.readTime,
          status: "published",
        }))
      );
      setLoading(false);
      return;
    }

    try {
      let queryBuilder = supabase
        .from("articles")
        .select("*", { count: "exact" })
        .eq("status", "published")
        .or(
          `title.ilike.%${q}%,summary.ilike.%${q}%,entrepreneur.ilike.%${q}%,country.ilike.%${q}%,sector.ilike.%${q}%`
        )
        .order("views", { ascending: false });

      if (category && category !== "Toutes catégories") queryBuilder = queryBuilder.eq("category", category);
      if (sector && sector !== "Tous les secteurs") queryBuilder = queryBuilder.eq("sector", sector);

      const { data, count } = await queryBuilder;
      setTotal(count ?? 0);
      setResults(
        (data ?? []).map((r) => ({
          id: String(r.id),
          title: String(r.title),
          summary: String(r.summary ?? ""),
          image: String(r.image ?? ""),
          entrepreneur: String(r.entrepreneur ?? ""),
          country: String(r.country ?? ""),
          countryFlag: String(r.country_flag ?? "🌍"),
          category: String(r.category ?? ""),
          sector: String(r.sector ?? ""),
          source: String(r.source ?? ""),
          sourceUrl: String(r.source_url ?? ""),
          date: String(r.date ?? ""),
          featured: Boolean(r.featured),
          views: Number(r.views ?? 0),
          readTime: Number(r.read_time ?? 5),
          status: "published",
        }))
      );
    } catch {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, category, sector]);

  useEffect(() => {
    search();
  }, [search]);

  return { results, loading, total };
}
